import { supabase } from "@/lib/supabase";
import { levenshteinDistance } from "@/lib/cat/translationMemory";

export type TargetOrigin =
    | "tm_exact"
    | "tm_fuzzy"
    | "mt_deepl"
    | "mt_google"
    | "mt_mymemory"
    | "lyra"
    | "human"
    | "unknown";

// Where an inserted suggestion came from. Panels report this when the user
// inserts a suggestion; the editor turns it into provenance on the segment.
export type SuggestionSource =
    | { kind: "tm"; score: number }
    | { kind: "mt"; provider: string }
    | { kind: "lyra" }
    // Text the translator put in themselves, such as a copy of the source.
    | { kind: "human" };

export interface Provenance {
    target_origin: TargetOrigin | null;
    origin_score: number | null;
    origin_text: string | null;
}

export const NO_PROVENANCE: Provenance = {
    target_origin: null,
    origin_score: null,
    origin_text: null,
};

export function mtOriginFromProvider(name: string): TargetOrigin {
    const n = name.toLowerCase();
    if (n.includes("deepl")) return "mt_deepl";
    if (n.includes("google")) return "mt_google";
    if (n.includes("mymemory") || n.includes("my memory")) return "mt_mymemory";
    return "unknown";
}

export function provenanceFromSource(source: SuggestionSource, text: string): Provenance {
    if (source.kind === "tm") {
        // Only a true 100 is exact: a 99.6 must not round up to exact.
        const exact = source.score >= 100;
        return {
            target_origin: exact ? "tm_exact" : "tm_fuzzy",
            origin_score: exact ? 100 : Math.min(99, Math.round(source.score)),
            origin_text: text,
        };
    }
    if (source.kind === "mt") {
        return { target_origin: mtOriginFromProvider(source.provider), origin_score: null, origin_text: text };
    }
    if (source.kind === "human") {
        return { target_origin: "human", origin_score: null, origin_text: null };
    }
    return { target_origin: "lyra", origin_score: null, origin_text: text };
}

const EDIT_PCT_MAX_CHARS = 2000;

// Normalised Levenshtein distance between the inserted suggestion and the final
// target, 0-100 with one decimal. Null when it does not apply.
export function editPercentage(
    origin: string | null | undefined,
    originText: string | null | undefined,
    finalText: string,
): number | null {
    if (!originText || !finalText || origin === "human" || !origin) return null;
    if (originText.length > EDIT_PCT_MAX_CHARS || finalText.length > EDIT_PCT_MAX_CHARS) return null;
    const maxLen = Math.max(originText.length, finalText.length);
    if (maxLen === 0) return 0;
    const pct = (levenshteinDistance(originText, finalText) / maxLen) * 100;
    return Math.round(pct * 10) / 10;
}

export function countWords(text: string): number {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
}

const IDLE_LIMIT_MS = 60_000;

// Counts milliseconds a segment is focused, the tab is visible and there was
// keyboard or mouse activity in the last 60 seconds. In memory only.
export class ActiveTimeTracker {
    private ms = new Map<string, number>();
    private activeId: string | null = null;
    private lastTick = Date.now();
    private lastActivity = Date.now();

    noteActivity() {
        this.lastActivity = Date.now();
    }

    tick() {
        const now = Date.now();
        if (
            this.activeId &&
            typeof document !== "undefined" &&
            document.visibilityState === "visible" &&
            now - this.lastActivity <= IDLE_LIMIT_MS
        ) {
            this.ms.set(this.activeId, (this.ms.get(this.activeId) || 0) + (now - this.lastTick));
        }
        this.lastTick = now;
    }

    setActive(id: string | null) {
        this.tick();
        this.activeId = id;
        this.lastActivity = Date.now();
    }

    // Returns the time recorded for a segment and starts it again from zero.
    consume(id: string): number {
        this.tick();
        const v = Math.round(this.ms.get(id) || 0);
        this.ms.delete(id);
        return v;
    }
}

export interface SegmentEventInput {
    userId: string;
    projectId: string;
    sourceLanguage: string;
    targetLanguage: string;
    event: "confirmed" | "unconfirmed";
    segment: {
        id: string;
        source_text: string;
        target_text: string;
        target_origin?: string | null;
        origin_score?: number | null;
        origin_text?: string | null;
    };
    activeMs: number;
}

// Fire and forget: never awaited by callers, never throws.
export function recordSegmentEvents(inputs: SegmentEventInput[]): void {
    if (inputs.length === 0) return;
    try {
        const rows = inputs.map((i) => {
            const origin = i.segment.target_origin || "unknown";
            return {
                user_id: i.userId,
                project_id: i.projectId,
                segment_id: i.segment.id,
                event: i.event,
                origin,
                origin_score: i.segment.origin_score ?? null,
                edit_pct:
                    i.event === "confirmed"
                        ? editPercentage(origin, i.segment.origin_text, i.segment.target_text || "")
                        : null,
                source_words: countWords(i.segment.source_text || ""),
                source_chars: (i.segment.source_text || "").length,
                active_ms: i.event === "confirmed" ? i.activeMs : 0,
                source_language: i.sourceLanguage,
                target_language: i.targetLanguage,
            };
        });
        void (async () => {
            for (let n = 0; n < rows.length; n += 200) {
                try {
                    const { error } = await supabase.from("cat_segment_events").insert(rows.slice(n, n + 200));
                    if (error) console.warn("Segment events not recorded:", error.message);
                } catch (e) {
                    console.warn("Segment events not recorded:", e);
                }
            }
        })();
    } catch (e) {
        console.warn("Segment events not recorded:", e);
    }
}
